import { colors as themeColors } from '../utils/theme';
import { fonts } from '../utils/theme';
import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── CSS keyframes ────────────────────────────────────────────────────────────
// All animations use ONLY transform / opacity → GPU compositor only.
// CSS @keyframes cannot be blocked by JS thread / React re-renders.
const CSS = `
  @keyframes _rl_in        { from { opacity:0 }                          to { opacity:1 } }
  @keyframes _rl_up        { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:translateY(0) } }
  @keyframes _rl_spin      { to   { transform:rotate(360deg) } }
  @keyframes _rl_sweep     { from { transform:scaleX(0) }                to { transform:scaleX(1) } }
  @keyframes _rl_pulse_ring {
    0%   { transform:scale(1);   opacity:0.6 }
    60%  { transform:scale(1.14);opacity:0   }
    100% { transform:scale(1.14);opacity:0   }
  }
`;

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected || document.getElementById('__rl_styles')) return;
  const el = document.createElement('style');
  el.id = '__rl_styles';
  el.textContent = CSS;
  document.head.appendChild(el);
  stylesInjected = true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ease = 'cubic-bezier(0.16,1,0.3,1)';

const S = {
  // Overlay — instant opacity fade (220ms). No slide, no clip — just confident takeover.
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(160deg,#091428 0%,#0d2050 45%,#1460d4 100%)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 0,
    animation: `_rl_in 0.22s ease-out both`,
    overflow: 'hidden',
  },
  // Dot grid texture
  grid: {
    position: 'absolute', inset: 0, opacity: 0.055,
    backgroundImage: `radial-gradient(circle,${themeColors.neutral.surface} 1px,transparent 1px)`,
    backgroundSize: '30px 30px',
    pointerEvents: 'none',
  },
  // Logo
  logo: {
    animation: `_rl_up 0.4s ${ease} 0.1s both`,
    marginBottom: 40,
  },
  // Ring wrapper (positions ring + avatar together)
  ringWrap: {
    position: 'relative', width: 88, height: 88,
    animation: `_rl_up 0.4s ${ease} 0.18s both`,
    marginBottom: 28,
  },
  // Spinning arc ring (border-top trick — only transform:rotate animated)
  ring: {
    position: 'absolute', inset: 0,
    borderRadius: '50%',
    border: '2.5px solid rgba(255,255,255,0.15)',
    borderTopColor: 'rgba(255,255,255,0.9)',
    borderRightColor: 'rgba(255,255,255,0.45)',
    animation: `_rl_spin 0.85s linear infinite`,
  },
  // Soft pulse shadow ring (scale-out pulse)
  pulseRing: {
    position: 'absolute', inset: -6,
    borderRadius: '50%',
    border: '1.5px solid rgba(255,255,255,0.22)',
    animation: `_rl_pulse_ring 1.8s ease-out 0.5s infinite`,
  },
  // Avatar circle (initials)
  avatar: {
    position: 'absolute', inset: 8,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,rgba(255,255,255,0.18),rgba(255,255,255,0.08))',
    border: '1.5px solid rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: themeColors.neutral.surface, fontWeight: 700, fontSize: '1.4rem',
    fontFamily: fonts.family,
    letterSpacing: '-0.01em',
  },
  // "Signing out of ReonBook"
  title: {
    color: 'rgba(255,255,255,0.92)',
    fontSize: '0.92rem', fontWeight: 600,
    fontFamily: fonts.family,
    letterSpacing: '0.01em',
    animation: `_rl_up 0.4s ${ease} 0.26s both`,
    marginBottom: 6,
  },
  // User name
  subtitle: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: '0.75rem', fontWeight: 400,
    fontFamily: fonts.family,
    letterSpacing: '0.02em',
    animation: `_rl_up 0.4s ${ease} 0.32s both`,
    marginBottom: 36,
  },
  // Progress track
  track: {
    width: 160, height: 2,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 2, overflow: 'hidden',
    animation: `_rl_up 0.3s ${ease} 0.38s both`,
  },
  // Progress fill — scaleX sweep
  fill: {
    height: '100%', borderRadius: 2,
    background: 'linear-gradient(90deg,rgba(255,255,255,0.4),rgba(255,255,255,0.9))',
    transformOrigin: 'left',
    animation: `_rl_sweep 0.85s ${ease} 0.5s both`,
  },
  // Bottom watermark
  watermark: {
    position: 'absolute', bottom: 28,
    color: 'rgba(255,255,255,0.18)',
    fontSize: '0.62rem', fontWeight: 600,
    fontFamily: fonts.family,
    letterSpacing: '0.22em',
    animation: `_rl_in 0.4s ease 0.5s both`,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function LogoutTransition() {
  const { logoutPending, commitLogout, user } = useAuth();
  const navigate   = useNavigate();
  const handledRef = useRef(false);

  useEffect(ensureStyles, []);

  useEffect(() => {
    if (!logoutPending) { handledRef.current = false; return; }
    if (handledRef.current) return;
    handledRef.current = true;

    // Wait for CSS animations to settle before committing logout
    const timer = setTimeout(async () => {
      await commitLogout();
      navigate('/', { replace: true });
    }, 950);

    return () => clearTimeout(timer);
  }, [logoutPending, commitLogout, navigate]);

  const name    = user?.EmployeeName || user?.UserName || '';
  const initial = name ? name.trim()[0].toUpperCase() : 'U';

  return (
    <AnimatePresence>
      {logoutPending && (
        // Framer Motion only handles EXIT — simple opacity fade to reveal login page
        <motion.div
          key="rl"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeIn' }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
        >
          {/* ── All entry animations are pure CSS (@keyframes) ── */}
          <div style={S.overlay}>

            {/* Dot texture */}
            <div style={S.grid} />

            {/* Logo */}
            <div style={S.logo}>
              <img
                src="/web01.svg"
                alt="ReonBook"
                style={{ height: 36, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
              />
            </div>

            {/* Spinner ring + avatar */}
            <div style={S.ringWrap}>
              <div style={S.pulseRing} />
              <div style={S.ring} />
              <div style={S.avatar}>{initial}</div>
            </div>

            {/* Text */}
            <div style={S.title}>Signing out of ReonBook</div>
            {name && <div style={S.subtitle}>{name}</div>}

            {/* Progress bar */}
            <div style={S.track}>
              <div style={S.fill} />
            </div>

            {/* Watermark */}
            <div style={S.watermark}>REONBOOK</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
